import { useState, useRef, useEffect, useReducer, memo } from "react";
import WebSocketService from "../services/WebSocketService";
import "./SpeechBubbleChat.css";
import { useWalkthrough } from "../services/WalkthroughContext";
import { isCurrentlyListening } from "../services/OpenAIVoiceService";

import MicrophoneIcon from "../icons/microphone.svg";
import NodesIcon from "../icons/nodes.svg";
import { FaPlus } from "react-icons/fa6";
const logo = process.env.PUBLIC_URL + "/assets/walkthrough/echo_avatar.svg";

const MessageList = memo(
  ({ messages, getUserText, getProcessedAttachments, logo }) => {
    return messages.map((msg, i) => {
      // Determine severity class for agent messages
      const severity = msg.severity || "green"; // default to green if not set
      const severityClass = msg.role === "agent" ? `severity-${severity}` : "";

      // Normalize agent/user message container
      if (msg.type === "instruction") {
        return (
          <div
            key={`msg-${i}-instruction`}
            className="message instruction"
          >
            {getUserText(msg.data)}
          </div>
        );
      }

      if (msg.data !== undefined && msg.role === "agent") {
          return (
            <div className="agent-message-row" key={`msg-${i}-agent`}>
              <div className="message-avatar">
                <img src={logo} alt="Echo" />
              </div>
              <div
                className={`message agent ${severityClass}`}
              >
                <div className="message-content">
                  <div className="message-text">{getUserText(msg.data)}</div>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="agent-message-attachments">
                      {msg.attachments.map((attachment, index) => (
                        <div key={`${i}-attachment-${index}`}>
                          {getProcessedAttachments(attachment, msg.data)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
      }

      // User message
      return (
        <div
          key={`msg-${i}-user`}
          className={`message ${msg.role}`}
        >
          <div className="message-content">
            <div className="message-text">{getUserText(msg.data)}</div>
          </div>
          {msg.attachments &&
            msg.attachments.length > 0 &&
            msg.attachments.map((attachment, index) => (
              <div
                className="user-message-attachments"
                key={`${i}-attachment-${index}`}
              >
                {getProcessedAttachments(attachment, "user")}
              </div>
            ))}
        </div>
      );
    });
  }
);

export default function SpeechBubbleChat({
  externalPrompt,
  messages,
  dispatchMessage,
  currentProject,
  handleProjectShare,
  flyToLocation,
}) {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const [animThinking, setAnimThinking] = useState(false);
  const [isActivelyListening, setIsActivelyListening] = useState(false);
  const { currentStep, next, listening, toggleListening } = useWalkthrough();

  // Poll listening status for UI feedback
  useEffect(() => {
    const interval = setInterval(() => {
      setIsActivelyListening(isCurrentlyListening());
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const handleSend = async (overrideText) => {
    const finalText = overrideText || input;
    if (!finalText) return;
    setInput("");
    // setAnimThinking(true);

    let payload = {
      type: "text",
      data: finalText,
      role: "user",
      attachments: [],
    };

    console.log(payload);

    dispatchMessage({ type: "ADD_MESSAGE", message: { ...payload } });

    WebSocketService.sendMessage(payload);
  };

  const handlePayloadSend = (payload) => {
    dispatchMessage({ type: "ADD_MESSAGE", message: { ...payload } });

    WebSocketService.sendMessage(payload);
  };

  useEffect(() => {
    if (externalPrompt) {
      handlePayloadSend(externalPrompt);
    }
  }, [externalPrompt]);

  const handleMessage = (_, message) => {
    // console.log(messages)

    switch (message?.type) {
      case "message":
      case "text":
        setAnimThinking(false);
        dispatchMessage({
          type: "ADD_MESSAGE",
          message: { ...message },
        });
        break;
      // case "plan":
      //   setAnimThinking(false);
      //   // console.log("Plan:", message.data);

      //   if (message.data.reasoning) {
      //     dispatchMessage({
      //       type: 'ADD_MESSAGE',
      //       message: {
      //         type: "text",
      //         data: message.data.reasoning,
      //         role: "agent",
      //         attachments: [{
      //           type: "dag",
      //           data: message.data.steps
      //         }]
      //       }
      //     });
      //   }
      //   break;
      case "ux":
        // process ux commands
        switch (message.instruction) {
          case "processing":
            setAnimThinking(true);
            break;
          case "loadProject":
            // console.log("Loading project:", message.data);
            dispatchMessage({ type: "SET_MESSAGES", messages: message.data });
            console.log("messages: ", message.data);
            const lastMessage = message.data[message.data.length - 1];
            if (lastMessage && lastMessage.type === "ux") {
              handleMessage(null, lastMessage);
            }
            // console.log("messages: ", messages)
            break;
          case "move_camera":
            if (
              typeof flyToLocation === "function" &&
              Array.isArray(message.data)
            ) {
              console.log("Moving camera to:", message.data);
              flyToLocation(message.data);
            } else {
              console.error(
                "Invalid flyToLocation or coordinates:",
                flyToLocation,
                message.data
              );
            }
            break;
          case "progress_update":
            // console.log("PROGRESS UPDATE:")
            if (message.data && message.data.id) {
              // console.log(workflowData)
            }
            break;
        }
        break;
      case "error":
        setAnimThinking(false);
        dispatchMessage({
          type: "ADD_MESSAGE",
          message: {
            type: "error",
            data: message.data,
            role: "agent",
          },
        });

        break;
      case "logging":
        // ignore, it'll get picked up by the service
        break;
      default:
        console.log("Unknown message type:", message?.type);
        break;
    }
    // console.log("messages", messages)
  };

  useEffect(() => {
    WebSocketService.addCallback(handleMessage);
    return () => WebSocketService.removeCallback(handleMessage);
  }, []);

  // Track if this is the first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: isFirstRender.current ? "auto" : "smooth" });
      isFirstRender.current = false;
    }
  }, [messages]);

  const getUserText = (payload) => {
    if (payload === undefined) {
      return "unknown";
      // return JSON.stringify(payload);
    }

    if (typeof payload === "string") {
      return payload;
    } else if (typeof payload === "object") {
      switch (payload.type) {
        case "text":
          return payload.data;
        case "message":
          return payload.data;
        case "plan":
          return payload.data.reasoning;
        case "audio":
          return "audio";
        case "image":
          return "image";
        case "video":
          return "video";
        case "document":
          return "document";
        case "location":
          return "location";
        case "contact":
          return "contact";
        case "error":
          return payload.data;
        default:
          return JSON.stringify(payload);
      }
    }
  };

  const getProcessedAttachments = (payload, text) => {
    // console.log("getProcessedAttachments", payload)
    if (payload === undefined) {
      return "unknown";
    }
    // Check if payload.type is undefined, if so try payload.schema
    if (payload.type === undefined && payload.schema !== undefined) {
      payload.type = payload.schema;
    }
    switch (payload.type) {
      case "search_results":
        // console.log("in case search results")
        let newPayloads = [];
        payload.data.forEach((row) => {
          let newPayload = {
            src: "",
            bbox: [],
            season: "",
            date: "",
            desc: "",
          };
          row.forEach((item) => {
            if (item.type === "image/png") {
              newPayload.src = item.data;
            } else if (item.type === "bbox") {
              newPayload.bbox = [
                parseFloat(item.data[0]).toFixed(4),
                parseFloat(item.data[1]).toFixed(4),
              ];
            } else if (item.type === "datetime") {
              newPayload.date = item.data;
            } else if (item.type === "season") {
              newPayload.season = item.data;
            } else if (item.type === "text") {
              newPayload.desc = item.data;
            }
          });
          newPayloads.push(newPayload);
        });

        return (
          <>
            {newPayloads.length <= 3 ? (
              // Case: 1-3 images → Render each separately
              newPayloads.map((image, index) => {
                // Check if src is a file path or base64
                const isFilePath = typeof image.src === 'string' && (image.src.startsWith('./') || image.src.startsWith('/') || image.src.startsWith('http') || image.src.startsWith('assets/'));
                const imgSrc = isFilePath ? image.src : `data:image/png;base64,${image.src}`;
                return (
                <div className="message-images" key={index}>
                  <img
                    src={imgSrc}
                    alt={`attachment-${index}`}
                  />
                  {/* <button
                    className="aag-button"
                    style={{ width: "min-content" }}
                    onClick={() => {
                      // setShowDataPane(true);
                      // setDataPaneImages(prev => [...prev, image]);
                    }}
                  >
                    <FaPlus />
                    <div>Add</div>
                  </button> */}
                </div>
                );
              })
            ) : (
              // Case: 4+ images → Show a 2x2 grid of first 4 images
              <div>
                <div className="image-grid-container">
                  {newPayloads.slice(0, 4).map((image, index) => {
                    // Check if src is a file path or base64
                    const isFilePath = typeof image.src === 'string' && (image.src.startsWith('./') || image.src.startsWith('/') || image.src.startsWith('http') || image.src.startsWith('assets/'));
                    const imgSrc = isFilePath ? image.src : `data:image/png;base64,${image.src}`;
                    return (
                    <div className="message-images" key={index}>
                      <img
                        src={imgSrc}
                        alt={`attachment-${index}`}
                      />
                    </div>
                    );
                  })}
                </div>
                {/* <button
                  className="aag-button"
                  onClick={() => {
                    // setShowDataPane(true);
                    // setDataPaneImages((prev) => [...prev, ...newPayloads]);
                  }}
                >
                  <FaPlus />
                  <div>Add {newPayloads.length}</div>
                </button> */}
              </div>
            )}
          </>
        );

      case "image/png":
        if (text == "user") {
          console.log("in case image/png", text);
          // Check if data is a file path or base64
          const isFilePath = typeof payload.data === 'string' && (payload.data.startsWith('./') || payload.data.startsWith('/') || payload.data.startsWith('http') || payload.data.startsWith('assets/'));
          const imgSrc = isFilePath ? payload.data : `data:image/png;base64,${payload.data}`;
          return (
            <div className="message-images" style={{ position: "relative" }}>
              <img
                style={{ width: "100%" }}
                src={imgSrc}
                alt="attachment"
              />
            </div>
          );
        }
        // Regexs to match latitude/longitude, season, and full description
        const latLonMatch = text.match(
          /\(?([-+]?\d*\.\d+)[,|\s]+([-+]?\d*\.\d+)\)?/
        );
        const seasonMatch = text.match(/\b(summer|winter|fall|spring)\b/i);
        const descMatch = text.match(/Full description:\s*(.*)/i);

        const lat = latLonMatch ? parseFloat(latLonMatch[1]).toFixed(4) : null;
        const lon = latLonMatch ? parseFloat(latLonMatch[2]).toFixed(4) : null;

        const season = seasonMatch
          ? seasonMatch[1].charAt(0).toUpperCase() + seasonMatch[1].slice(1)
          : "";
        const desc = descMatch
          ? descMatch[1].trim().toLowerCase() == "none" || ""
            ? ""
            : descMatch[1].trim()
          : "";

        // Check if data is a file path or base64
        const isFilePath = typeof payload.data === 'string' && (payload.data.startsWith('./') || payload.data.startsWith('/') || payload.data.startsWith('http') || payload.data.startsWith('assets/'));
        const imgSrc = isFilePath ? payload.data : `data:image/png;base64,${payload.data}`;

        let newImage = {
          src: payload.data,
          bbox: lat && lon ? [lat, lon] : [],
          season,
          desc,
        };

        // console.log("newImage", newImage);

        return (
          <div className="message-images">
            <img
              style={{ width: "100%" }}
              src={imgSrc}
              alt="attachment"
            />
            {/* <button
              className="aag-button"
              style={{ width: "min-content" }}
              onClick={() => {
                // setShowDataPane(true);
                // setDataPaneImages(prev => [...prev, newImage]);
              }}
            >
              <FaPlus />
              <div>Add</div>
            </button> */}
          </div>
        );
      case "dag":
        return (
          <button
            className="aag-button"
            onClick={() => {
              // setWorkflowData(payload.data);
            }}
          >
            <img src={NodesIcon} /> View AAG
          </button>
        );
      case "add_layer_KMLTILES":
        return (
          <button
            onClick={() => {
              alert("Not implemented yet, sorry");
            }}
          >
            Add KML Layer
          </button>
        );
      default:
        return JSON.stringify(payload);
    }
  };

  return (
    <div className="speech-bubble">
      <div className="speech-bubble-title">{currentProject["name"]}</div>
      <div className="speech-bubble-content">
        <div className="messages">
          <MessageList
            messages={messages}
            getUserText={getUserText}
            getProcessedAttachments={getProcessedAttachments}
            logo={logo}
          />
          <div ref={messagesEndRef} />
        </div>
        {animThinking && (
          <div className="thinking">
            <span>Received, processing your request...</span>
            <video autoPlay loop muted>
              <source
                src="assets/green_echo_rotating_loop_fix_alpha.webm"
                type="video/webm"
              />
              <source
                src="assets/green_echo_rotating_loop_fix_alpha.mp4"
                type="video/mp4"
              />
            </video>
          </div>
        )}
      </div>
      {/* Next control for walkthrough */}
      {/* Next control for walkthrough
          Behavior: require explicit user interaction between steps. If walkthrough
          is in listening mode and the step expects speech, prefer voice; otherwise
          show the Next button. Steps that set nextOn === 'none' never show Next. */}
      {(() => {
        if (!currentStep) return null;
        if (currentStep.nextOn === "none") return null;
        const expectsSpeech =
          (currentStep.expectedInput || "").includes("speech") ||
          (Array.isArray(currentStep.acceptPhrases) &&
            currentStep.acceptPhrases.length > 0);

        // When we're in listening mode and the step expects speech, show a
        // microphone button instead of hiding the control entirely. This
        // gives the user a clear affordance (and a way to toggle listening
        // off) without wiring full speech recognition here.
        const showMic = listening && expectsSpeech;
        if (showMic) {
          return (
            <div className="next-button-container">
              <button
                className={`next-button microphone-button ${isActivelyListening ? 'listening-active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Toggle listening off so the user can resume with Next.
                  try {
                    if (typeof toggleListening === "function") toggleListening();
                  } catch (err) {
                    /* ignore */
                  }
                }}
                aria-label={isActivelyListening ? "Listening... Click to stop" : "Microphone"}
                title={isActivelyListening ? "Listening for voice input... Click to stop" : "Click to disable voice input"}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 12V13C20 17.4183 16.4183 21 12 21C7.58172 21 4 17.4183 4 13V12M12 17C9.79086 17 8 15.2091 8 13V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V13C16 15.2091 14.2091 17 12 17Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {isActivelyListening && (
                  <span className="listening-indicator">Listening...</span>
                )}
              </button>
            </div>
          );
        }

        // Fall back to the existing Next button when not showing the mic
        return (
          <div className="next-button-container">
            <button
              className="next-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                next();
              }}
              aria-label="Next step"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M8 5l8 7-8 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        );
      })()}
    </div>
  );
}
